import React, { useEffect, useState } from 'react'
import Select from "react-select"
import Creatable from "react-select/creatable"
import pluralize from 'pluralize'
import { Drawer, DatePicker, message, Dropdown, Menu, Upload, Switch } from 'antd'

import { AiOutlineDown } from 'react-icons/ai'
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'

import { server, useListData, usePet, useToggle } from '../../modules'

import Modal from '../reusable/modal'
import Receive from '../reusable/receive'
import Confirm from '../reusable/confirm'
import SelectLabel from '../reusable/selectLabel'

import Ingredients from '../modals/ingredients'
import Variant from '../modals/variant'
import EditVariant from '../modals/editVariant'

import EditLinkedSaleItem from './editLinkedSaleItem'

const { RangePicker } = DatePicker
const preauthCategoryOptions = [
    { label: 'Surgical', value: 'SURGICAL' },
    { label: 'Imaging', value: 'IMAGING' },
    { label: 'Renal', value: 'RENAL' },
    { label: 'Oncology', value: 'ONCOLOGY' },
    { label: 'Optical', value: 'OPTICAL' },
]

const assignGroupCopy = {
    item: {
        title: 'Assign Billing & Inventory Group',
        fieldLabel: 'Billing & Inventory Group',
        helper: 'Controls where clinicians can order this item and which default store can be used.',
        placeholder: 'Select billing & inventory group',
    },
    routing: {
        title: 'Assign Department Queue',
        fieldLabel: 'Department Queue',
        helper: 'Controls which department queue this item routes to after billing.',
        placeholder: 'Select department queue',
    },
}

const AssignGroup = ({ category, toggle, visible, groups, onComplete }) => {
    const [group, setGroup] = useState()
    const copy = assignGroupCopy[category] ?? assignGroupCopy.item

    const save = () => {
        onComplete(group)
        toggle()
    }

    return (
        <Modal
            w={ 450 }
            title={copy.title}
            toggle={toggle}
            visible={visible}>
            <div className="w-full bg-white">
                <div className="flex flex-wrap -mx-3 mb-3">
                    <div className="w-full px-3 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            {copy.fieldLabel}
                        </label>
                        <p className="text-gray-500 text-xs italic mb-2">{copy.helper}</p>
                        <Select
                            value={group}
                            options={groups}
                            onChange={setGroup}
                            placeholder={copy.placeholder} />
                    </div>
                </div>
            </div>
            <div className="flex justify-end">
                <button onClick={() => Confirm({
                    title: 'Save Changes?',
                    desc: 'Are you sure you want to save these changes?',
                    okText: 'Save Changes',
                    onOk: save
                })} type="submit" className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
            </div>
        </Modal>
    )
}

const EditSaleItem = ({ toggle, visible, item: itm, onPerform }) => {
    let type = itm?.type === 'variant' ? itm?.ref ? 'Sale Item' : 'Prepared Item' : itm.type

    const [itemGroup, setItemGroup] = useState(itm?.itemGroup ? { label: itm.itemGroup.name ?? itm.itemGroup.label, value: itm.itemGroup._id ?? itm.itemGroup.value ?? itm.itemGroup } : null)
    const [routingGroup, setRoutingGroup] = useState(itm?.routingGroup ? { label: itm.routingGroup.name ?? itm.routingGroup.label, value: itm.routingGroup._id ?? itm.routingGroup.value ?? itm.routingGroup } : null)
    const [pendingGroupAssignment, setPendingGroupAssignment] = useState(false)
    const [name, setName] = useState(itm?.name)
    const [category, setCategory] = useState({ label: itm?.category, value: itm?.category })
    const [invRef, setInvRef] = useState(itm?.ref && { label: itm?.ref.name, value: itm?.ref._id })
    const [index, setIndex] = useState()
    const [val, setVal] = useState(null)

    const [price, setPrice] = useState(itm?.price)
    const [variants, setVariants] = useState(itm?.variants ?? [])
    const [categories, setCategories] = useState([])
    const [itemGroups, setItemGroups] = useState([])
    const [routingGroups, setRoutingGroups] = useState([])
    const [accounts, setAccounts] = useState([])
    const [account, setAccount] = useState(itm?.account)
    const [form, setForm] = useState(itm?.form ? { label: itm.form.name, value: itm.form._id ?? itm.form } : null)
    const [preauthRequired, setPreauthRequired] = useState(!!itm?.preauthRequired)
    const [preauthCategory, setPreauthCategory] = useState(itm?.preauthCategory ? preauthCategoryOptions.find(opt => opt.value === itm.preauthCategory) : null)

    const [itemType] = useState({ label: type, value: type })
    const [ingredients, setIngredients] = useState(itm?.ingredients)
    const [imageUrl, setImageUrl] = useState(itm?.image ? `${server}/uploads/sale_items/${itm?.image}` : null)
    const [image, setImage] = useState(null)
    const [string, setString] = useState('')
    const [conversionRate, setConversionRate] = useState(itm?.conversionRate ?? 1)
    const [maxDiscount, setMaxDsc] = useState(itm?.maxDiscount ?? 0)
    const [selected, setSelected] = useState()
    const [lists, setLists] = useState([])

    const [vrnt, toggleVariant] = useToggle()
    const [linked, toggleLinked] = useToggle()
    const [imgLoading, setImgLoading] = useState(false)
    const [assignGroup, toggleAssignGroup] = useToggle()
    const [variantsModal, toggleVariantsModal] = useToggle()
    const [ingredientsModal, toggleIngredientsModal] = useToggle()

    const [{ loading: invLoading, fetch: fetchInvItems }, { list: items = [] }] = useListData('inventoryItems', { string }, { notify: true }, 'items')
    const [{ loading: formsLoading, fetch: fetchForms }, { list: forms = [] }] = useListData('inputForms', { string }, { notify: true }, 'forms', 100)

    const hasVariants = itm?.variants?.length > 0
    const [,
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('accounts', { string, types: ['Expenses', 'Cost of Goods Sold'] }, { notify: true, manual: false }, 'accounts', 100, ({ accounts: data }) => {
        let obj = {}

        data.forEach(acc => {
            if (!obj[acc.type])
                obj[acc.type] = []

            obj[acc.type].push({
                label: acc.name,
                value: acc._id
            })
        })

        let final = []
        for (let typ in obj) {
            final.push({
                label: typ,
                options: obj[typ]
            })
        }

        setAccounts(final)
    })

    const search = str => {
        if (str?.trim()?.length > 2) {
            setString(str)
            fetchInvItems()
        }
    }

    const onComplete = ({ }, notif, ops) => {
        onPerform()
        notif('Changes have been saved', ops('success'))

        setTimeout(toggle, 300)
    }

    const [{ loading }, execute] = usePet('editSaleItem', {
        id: itm?._id,
        name,
        category: category?.value,
        price,
        account: account?.value ?? account?._id ?? account,
        ref: invRef ? invRef._id : null,
        ingredients: ingredients?.map(itm => ({
            item: itm._id,
            quantity: itm.qty
        })),
        image,
        conversionRate,
        routingGroup: routingGroup?.value ?? null,
        itemGroup: itemGroup?.value ?? null,
        form: form?.value ?? null,
        preauthRequired,
        preauthCategory: preauthRequired ? preauthCategory?.value : null,
    }, { notify: true, hasFile: true, callback: onComplete, manual: true, load: { msg: 'Editing sale item...' } })

    const [, assignItemGroup] = usePet('setSaleItemGroup', {
        item: itm?._id,
        purpose: 'item',
        group: itemGroup?.value
    }, { notify: true, manual: true })

    useEffect(() => {
        if (!pendingGroupAssignment) return
        setPendingGroupAssignment(false)
        assignItemGroup()
    }, [pendingGroupAssignment]) // eslint-disable-line react-hooks/exhaustive-deps

    const [{ loading: dLoading }, deleteVariant] = usePet('deleteVariant', {
        id: itm?._id,
        index
    }, {
        notify: true, callback: ({ }, notif, ops) => {
            onPerform()
            notif('Item has been deleted', ops('success'))

            setTimeout(toggle, 300)
        }, manual: true, load: { msg: 'Deleting variant...' }
    })

    const [{ loading: catsLoading, error: catsError }] = usePet('saleItemCategories', null, { notify: true, callback: ({ data }) => setCategories(data) })

    const modify = e => {
        switch (e.target.name) {
            case 'name':
                setName(e.target.value)
                break

            case 'price':
                setPrice(e.target.value)
                break

            case 'max-discount':
                setMaxDsc(e.target.value)
                break

            case 'conversion-rate':
                setConversionRate(e.target.value)
                break

            default:
                break
        }
    }

    const getBase64 = (img, callback) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => callback(reader.result))
        reader.readAsDataURL(img)
    }

    const beforeUpload = file => {
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
        if (!isImage) {
            message.warning('Please select an image file')
            return false
        }

        const sizeOK = file.size / 1024 / 1024 <= 1
        if (!sizeOK) {
            message.warning('Please select an image smaller than 1MB')
            return false
        }

        setImageUrl(null)
        setImage(file)
        setImgLoading(true)

        setTimeout(() => {
            setImageUrl(URL.createObjectURL(file))
            setImgLoading(false)
        }, 1000)

        return false
    }

    const handleChange = info => {
        if (info.file.status === 'uploading') {
            setImgLoading(true)
            return
        }

        if (info.file.status === 'done') {
            getBase64(info.file.originFileObj, url => {
                setImageUrl(url)
                setImgLoading(false)
            })
        }
    }

    usePet('itemGroups', null, {
        notify: true, callback: ({ data }) => {
            data = data.filter(grp => grp.name !== 'Bill').map(grp => ({ label: grp.name, value: grp._id }))
            setItemGroups(data)
        }
    })

    usePet('routingGroups', null, {
        notify: true, callback: ({ data }) => {
            data = (data || []).map(grp => ({ label: grp.name, value: grp._id }))
            setRoutingGroups(data)
        }
    })

    usePet('priceLists', { limit: 100 }, {
        notify: true, callback: async ({ data }) => {
            let final = []

            for (let list of data.lists) {
                let eq = itm?.prices?.find(entry => entry?.priceList === list._id)

                list.price = (list.name === 'Cash' ? itm?.price : eq?.price) ?? 0
                list.formula = eq?.pricingFormula ?? ''
                list.type = eq?.type ?? list.type ?? "Direct Price"

                final.push(list)
            }

            setLists(final)
        }
    })

    const removeImage = () => {
        setImage(null)
        setImageUrl(itm?.image ? `${server}/uploads/sale_items/${itm?.image}` : null)
    }

    const deleteIngredient = i => {
        let temp = [...ingredients]
        temp = temp.filter((_, index) => i !== index)

        setIngredients(temp)
    }

    const addVariant = vrnt => {
        let temp = [...variants]
        temp.push(vrnt)

        setVariants(temp)
    }

    const newCategory = vl => {
        setVal(vl)
        toggleAssignGroup()
    }

    const addCategory = grp => {
        setItemGroup(grp)
        let temp = [...categories]
        temp.push(val)
        setCategories(temp)
        setCategory({ label: val, value: val })

        if (grp?.value) setPendingGroupAssignment(true)
    }

    const editVariant = vrnt => {
        let match
        for (let grp of accounts) {
            for (let acc of grp.options) {
                if (acc.value === vrnt.account) {
                    match = acc
                    break
                }
            }

            if (match) break
        }

        setSelected({
            ...vrnt,
            account: match
        })
        toggleLinked()
        // toggleVariant()

    }

    const deleteVrnt = i => {
        setIndex(i)
        Confirm({
            title: 'Delete Item?',
            message: 'Are you sure you want to delete this item?',
            onOk: deleteVariant,
            okText: 'Delete Item'
        })
    }

    const save = () => Confirm({
        title: 'Edit Item',
        message: 'Are you sure you want to edit this item?',
        onOk: execute,
        okText: 'Edit Item'
    })

    const close = () => {
        toggle()
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button disabled={loading} onClick={save} type="submit" className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 mr-2 py-2.5 text-center">Save</button>
                    <button onClick={close} className="text-white bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Close</button>
                </div>
            }
            width="600px">
            <p className="font-extrabold text-3xl mb-0">Edit Sale Item</p>
            <div className="divide-y divide-light-gray-400">
                {
                    vrnt &&
                    <EditVariant
                        parent={itm}
                        index={index}
                        visible={vrnt}
                        onPerform={() => {
                            onPerform()
                            setTimeout(() => {
                                toggleVariant()
                                close()
                            }, 300)
                        }}
                        toggle={toggleVariant} />
                }
                {
                    assignGroup &&
                    <AssignGroup
                        groups={itemGroups}
                        onComplete={addCategory}
                        toggle={toggleAssignGroup}
                        visible={assignGroup} />
                }
                {
                    variantsModal &&
                    <Variant
                        parent={itm}
                        onPerform={() => {
                            onPerform()
                            setTimeout(() => {
                                toggleVariantsModal()
                                close()
                            }, 300)
                        }}
                        setString={setString}
                        loading={invLoading}
                        fetch={fetchInvItems}
                        items={items}
                        type={itemType}
                        onSave={addVariant}
                        visible={variantsModal}
                        toggle={toggleVariantsModal} />
                }
                {
                    linked &&
                    <EditLinkedSaleItem
                        itm={selected}
                        type={ type }
                        variant={ true }
                        priceLists={lists}
                        visible={linked}
                        accounts={accounts}
                        onComplete={() => {
                            onPerform()
                            setTimeout(() => {
                                toggleVariantsModal()
                                close()
                            }, 300)
                        }}
                        toggle={toggleLinked} />
                }
                <Ingredients
                    ingredients={ingredients}
                    onSave={setIngredients}
                    visible={ingredientsModal}
                    toggle={toggleIngredientsModal} />
                {/* <div className="w-full flex flex-col md:flex-row"> */}
                {/* <div className="w-full md:w-3/12 h-auto text-center m-auto lg:block lg:w-5/12">
                        <p className="text-gray-500 text-xs italic">Image of Item</p>
                        <Upload
                            name="avatar"
                            listType="picture-card"
                            className="avatar-uploader"
                            showUploadList={false}
                            action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                            beforeUpload={beforeUpload}
                            onChange={handleChange}>
                            {
                                imageUrl ?
                                    <img
                                        src={imageUrl}
                                        alt="avatar"
                                        style={{ width: '100%' }} />
                                    :
                                    <div>
                                        {imgLoading ? <LoadingOutlined /> : <PlusOutlined />}
                                        <div style={{ marginTop: 8 }}>{imgLoading ? 'Generating preview...' : 'Upload image'}</div>
                                    </div>
                            }
                        </Upload>
                        {
                            image && <p onClick={removeImage} className="text-red-500 cursor-pointer text-xs italic">X Remove</p>
                        }
                    </div> */}
                {/* <div className="w-full lg:w-7/12 bg-white p-5"> */}
                <div className="flex flex-wrap -mx-3 pt-5 mb-3">
                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            Name
                        </label>
                        <input
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            id="grid-name"
                            onChange={modify}
                            value={name}
                            type="text"
                            name="name"
                            placeholder="Item A" />
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            Category
                        </label>
                        <p className="text-gray-500 text-xs italic mb-2">A display label only (e.g. "Doctor", "Lab"). Does not route orders or control ordering — use Department Queue and Billing & Inventory Group below.</p>
                        <Receive
                            loading={catsLoading}
                            error={catsError}>
                            <Creatable
                                value={category}
                                onCreateOption={val => {
                                    let temp = [...categories]
                                    temp.push(val)
                                    setCategories(temp)
                                    setCategory({ label: val, value: val })
                                }}
                                onChange={setCategory}
                                options={categories.map(cat => ({ label: cat, value: cat }))}
                                placeholder="Select Category" />
                        </Receive>
                    </div>
                </div>
                {
                    type === 'Sale Item' && !hasVariants &&
                    <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Conversion Rate
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-conversion-rate"
                                type="text"
                                name="conversion-rate"
                                value={conversionRate}
                                onChange={modify}
                                placeholder="1/2" />
                        </div>
                    </div>
                }
                <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                            Income Account
                        </label>
                        <Select
                            className="mb-3"
                            value={account}
                            onChange={setAccount}
                            formatGroupLabel={SelectLabel}
                            options={accounts}
                            placeholder="Select account" />
                    </div>
                    {
                        type === 'Service' && !hasVariants &&
                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                                Input Form
                            </label>
                            <Select
                                value={form}
                                className="mb-3"
                                options={forms.map(frm => ({ value: frm._id, label: frm.name }))}
                                onChange={setForm}
                                isLoading={formsLoading}
                                placeholder="Select Input Form" />
                        </div>
                    }
                </div>
                <div className="flex flex-wrap -mx-3 pt-2 mb-3">
                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                            Department Queue
                        </label>
                        <p className="text-gray-500 text-xs italic mb-2">Controls which department queue this item routes to after billing.</p>
                        <Select
                            isClearable
                            value={routingGroup}
                            onChange={setRoutingGroup}
                            options={routingGroups}
                            placeholder="Select department queue" />
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                            Billing & Inventory Group
                        </label>
                        <p className="text-gray-500 text-xs italic mb-2">Controls where clinicians can order this item and which default store can be used.</p>
                        <Select
                            isClearable
                            value={itemGroup}
                            onChange={setItemGroup}
                            options={itemGroups}
                            placeholder="Select billing & inventory group" />
                    </div>
                </div>
                {
                    type === 'Service' && !hasVariants &&
                    <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Requires Preauth
                            </label>
                            <div>
                                <Switch
                                    checked={preauthRequired}
                                    onChange={checked => {
                                        setPreauthRequired(checked)
                                        if (!checked) setPreauthCategory(null)
                                    }} />
                                <p className="text-gray-500 text-xs italic">Enable when this service needs insurer preauthorization before billing.</p>
                            </div>
                        </div>
                        {
                            preauthRequired &&
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                    Preauth Category
                                </label>
                                <Select
                                    value={preauthCategory}
                                    onChange={setPreauthCategory}
                                    options={preauthCategoryOptions}
                                    placeholder="Select category" />
                            </div>
                        }
                    </div>
                }
                {
                    itemType &&
                    <>
                        {
                            hasVariants &&
                            <div className="font-bold mt-3">
                                <div className="flex justify-between items-center">
                                    <p className="font-bold my-3">Variants</p>
                                    <button onClick={toggleVariantsModal} className="btn hisani-btn-green text-white my-3">
                                        <svg className="w-4 h-4 fill-current opacity-50 flex-shrink-0" viewBox="0 0 16 16">
                                            <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                        </svg>
                                        <span className="hidden xs:block ml-2">Add</span>
                                    </button>
                                </div>
                                {
                                    variants.length > 0 ?
                                        <table className="table-auto w-full mb-3">
                                            {/* Table header */}
                                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                <tr>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">No</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Variant</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Price</div>
                                                    </th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            {/* Table body */}
                                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                {
                                                    variants.map((variant, key) =>
                                                        <tr>
                                                            <td className="p-2 text-left">{key + 1}</td>
                                                            <td className="p-2 text-left">{variant.name}</td>
                                                            <td className="p-2 text-left">Ksh. {variant.price}</td>
                                                            <td className="p-2 text-left text-red-500 cursor-pointer">
                                                                <Dropdown
                                                                    overlay={
                                                                        <Menu>
                                                                            <Menu.Item onClick={() => editVariant(variant)}>View</Menu.Item>
                                                                            <Menu.Item onClick={() => deleteVrnt(key)}>Delete</Menu.Item>
                                                                        </Menu>
                                                                    }>
                                                                    <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                                </Dropdown>
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            </tbody>
                                        </table>
                                        :
                                        <div className="text-center my-4">
                                            <h3>Please click above to add variants</h3>
                                        </div>
                                }
                            </div>
                        }
                        <div className="flex flex-wrap -mx-3 pt-4 mb-3">
                            {
                                !hasVariants &&
                                <>
                                    <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Price
                                        </label>
                                        <input
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-price"
                                            value={price}
                                            onChange={modify}
                                            name="price"
                                            type="number"
                                            placeholder="XXXX" />
                                    </div>
                                    {
                                        itemType?.value === 'Sale Item' &&
                                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                                Conversion Rate
                                            </label>
                                            <input
                                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                id="grid-conversion-rate"
                                                type="text"
                                                name="conversion-rate"
                                                value={conversionRate}
                                                onChange={modify}
                                                placeholder="1/2" />
                                        </div>
                                    }
                                </>
                            }
                        </div>
                        {
                            !hasVariants ?
                                itemType?.value === 'Prepared Item' ?
                                    <div className="font-bold mt-3">
                                        <div className="flex justify-between items-center">
                                            <p className="font-bold my-3">Ingredients</p>
                                            <button onClick={toggleIngredientsModal} className="btn hisani-btn-green text-white my-3">
                                                <svg className="w-4 h-4 fill-current opacity-50 flex-shrink-0" viewBox="0 0 16 16">
                                                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                                </svg>
                                                <span className="hidden xs:block ml-2">Add</span>
                                            </button>
                                        </div>
                                        {
                                            ingredients.length > 0 ?
                                                <table className="table-auto w-full mb-3">
                                                    {/* Table header */}
                                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                        <tr>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">No</div>
                                                            </th>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">Selected</div>
                                                            </th>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">Quantity</div>
                                                            </th>
                                                            <th />
                                                        </tr>
                                                    </thead>
                                                    {/* Table body */}
                                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                        {
                                                            ingredients.map((ingredient, key) =>
                                                                <tr>
                                                                    <td className="p-2 text-left">{key + 1}</td>
                                                                    <td className="p-2 text-left">{ingredient?.item?.name}</td>
                                                                    <td className="p-2 text-left">{ingredient.quantity} {pluralize(ingredient?.item?.issuedIn ?? '', ingredient.quantity)}</td>
                                                                    <td onClick={() => deleteIngredient(key)} className="p-2 text-left text-red-500 cursor-pointer">X</td>
                                                                </tr>
                                                            )
                                                        }
                                                    </tbody>
                                                </table>
                                                :
                                                <div className="text-center my-4">
                                                    <h3>Please click above to add ingredients</h3>
                                                </div>
                                        }
                                    </div>
                                    : itemType?.value === 'Sale Item' ?
                                        <div className="flex flex-wrap -mx-3 pt-4 mb-6 bt-1 border-gray-200">
                                            <div className="w-full md:w-2/3 px-3 mb-6 md:mb-0">
                                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                                    Inventory Item
                                                </label>
                                                <Select
                                                    isLoading={invLoading}
                                                    loadingMessage={string => `Searching for "${string?.inputValue}"`}
                                                    onInputChange={search}
                                                    value={invRef}
                                                    onChange={setInvRef}
                                                    options={items.map(item => ({ label: item.name, value: item._id, ...item }))}
                                                    placeholder="Search for inventory item" />
                                                <p className="text-gray-500 text-xs italic">Which inventory item does it refer to?</p>
                                            </div>
                                        </div>
                                        : null
                                : null
                        }
                    </>
                }
                {/* </div>
                </div> */}

            </div>
        </Drawer>
    )
}

export default EditSaleItem
